import { useIntl } from 'react-intl';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Container } from '@/components/Container';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { productCardMessages } from '@/components/ProductCard/messages';
import type { SectionHeaderProps } from '@/components/SectionHeader';
import { SectionHeader } from '@/components/SectionHeader';
import type { ContentfulImageWithFocalPoint } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { generateDataTestId } from '@/lib/tests';
import { getSubheadingType } from '@/lib/title';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme';

import type { HeroProductListingProps } from '../../types';

import * as S from './TheCollectionsType.styles';

export const TheCollectionsType = ({
  backgroundColor,
  backgroundColorOpacity,
  collectionItemsCollection,
  heading,
  heroProductListingTitle: title,
  media,
  pageName,
  showBreadcrumbs,
  subheader,
  textAlignment,
}: HeroProductListingProps) => {
  const { paths } = useRouter();
  const { locale } = useRegionProvider();
  const { formatMessage } = useIntl();
  const headingText = title?.text ?? heading;

  return (
    <Container
      data-test-id='TheCollectionsTypeContainer'
      disableGutters
      maxWidth='xl'
    >
      <S.Header
        $backgroundColorOpacity={backgroundColorOpacity}
        backgroundColor={backgroundColor?.value}
        backgroundImage={media?.image?.url}
        data-test-id='TheCollectionsTypeHeader'
      >
        <Breadcrumb.Wrapper>
          {showBreadcrumbs && (
            <Breadcrumb
              data-test-id='TheCollectionsTypeBreadcrumb'
              items={[
                {
                  label: formatMessage(commonMessages.home),
                  target: paths.url(),
                },
                {
                  label: formatMessage(commonMessages.theCollections),
                  target: {
                    ...paths.watches.url(),
                    pathname: `${paths.watches.url().pathname}${
                      pageName ?? ''
                    }`,
                  },
                },
              ]}
            />
          )}
        </Breadcrumb.Wrapper>
        <Container data-test-id='TheCollectionsTypeHeaderContainer'>
          <S.Heading data-test-id='TheCollectionsTypeHeading'>
            {headingText && (
              <SectionHeader
                color={theme.colors.black}
                data-test-id='TheCollectionsTypeSectionHeaderHeading'
                disableGutters
                htmlElementTag={
                  (title?.headingSeoTag ?? 'h3') as keyof HTMLElementTagNameMap
                }
                sizeDesktop={50}
                sizeMobile={40}
                text={headingText}
                textAlign={textAlignment as SectionHeaderProps['textAlign']}
              />
            )}
            {subheader && (
              <SectionHeader
                color={theme.colors.black}
                data-test-id='TheCollectionsTypeSectionHeaderSubheading'
                disableGutters
                htmlElementTag={getSubheadingType(
                  (title?.headingSeoTag ?? 'h3') as keyof HTMLElementTagNameMap,
                )}
                sizeDesktop={20}
                sizeMobile={20}
                text={subheader}
                textAlign={textAlignment as SectionHeaderProps['textAlign']}
              />
            )}
          </S.Heading>

          <S.ScrollableRow data-test-id='TheCollectionsTypeScrollableRow'>
            {collectionItemsCollection?.items.map((item, index) => (
              <LinkWithLocale
                key={item?.sys.id}
                aria-label={getLinkAriaLabel(
                  '/collections/' +
                    encodeURIComponent(
                      item?.collectionName?.replace(/\s/g, '-') ?? '',
                    )?.toLocaleLowerCase(),
                  locale.code,
                )}
                data-test-id={`TheCollectionsTypeCollectionLink-${index}`}
                href={
                  '/collections/' +
                  encodeURIComponent(
                    item?.collectionName?.replace(/\s/g, '-') ?? '',
                  )?.toLocaleLowerCase()
                }
              >
                <S.ImageContainer
                  data-test-id={`TheCollectionsTypeImageContainer-${index}`}
                >
                  {(item?.image as ContentfulImageWithFocalPoint)?.image && (
                    <>
                      <AspectRatioImage
                        alt={formatMessage(productCardMessages.linkToPlp, {
                          collection: item?.collectionName,
                        })}
                        aspectRatio={{ x: 3, y: 4 }}
                        className='collection-header'
                        data-test-id={`TheCollectionsTypeAspectRatioImage-${index}`}
                        focalPoint={{
                          x:
                            (item?.image as ContentfulImageWithFocalPoint)
                              ?.focalPoint?.focalPoint?.x ?? 0,
                          y:
                            (item?.image as ContentfulImageWithFocalPoint)
                              ?.focalPoint?.focalPoint?.y ?? 0,
                        }}
                        loaderProps={{ height: 532 }}
                        loading='eager'
                        originalImageDimensions={{
                          x: (item?.image as ContentfulImageWithFocalPoint)
                            ?.image?.width as number,
                          y: (item?.image as ContentfulImageWithFocalPoint)
                            ?.image?.height as number,
                        }}
                        showFallbackPlaceholder={false}
                        sizes='25vw'
                        src={
                          (item?.image as ContentfulImageWithFocalPoint)?.image
                            ?.url as string
                        }
                        useLoader={false}
                        width='100%'
                        {...generateDataTestId('productCard', 'image')}
                      />
                      <S.CollectionName
                        data-test-id={`TheCollectionsTypeCollectionName-${index}`}
                      >
                        <span>{item?.collectionName}</span>
                      </S.CollectionName>
                    </>
                  )}
                </S.ImageContainer>
              </LinkWithLocale>
            ))}
          </S.ScrollableRow>
        </Container>
      </S.Header>
    </Container>
  );
};
